import { useEffect, useRef, useState } from "react";
import { LuLandmark, LuBrain, LuCastle, LuCircleDot, LuCoins, LuCrown, LuDice5, LuDices, LuFlag, LuGem, LuGlobe, LuGrid3X3, LuLayoutGrid, LuLayers, LuShip, LuSwords, LuZap } from "react-icons/lu";
import { SUBJECTS } from "../../shared/constants";
import { ApiError } from "../api";
import { useRecent, useTween } from "./juice";

export function Spinner({ label = "Henter…" }: { label?: string }) {
  return (
    <div className="row" role="status" aria-live="polite" style={{ justifyContent: "center", padding: 30 }}>
      <div className="spinner" aria-hidden="true" />
      <span className="muted">{label}</span>
    </div>
  );
}

export function ErrorBox({ error, retry }: { error: ApiError | Error | string | null; retry?: () => void }) {
  if (!error) return null;
  const msg = typeof error === "string" ? error : error.message;
  const offline = error instanceof ApiError && error.status === 0;
  return (
    <div className={`notice ${offline ? "warn" : "error"}`} role="alert">
      <strong>{offline ? "Ingen forbindelse" : "Hov!"}</strong> {msg}
      {retry && (
        <div style={{ marginTop: 8 }}>
          <button className="btn small ghost" onClick={retry}>
            Prøv igen
          </button>
        </div>
      )}
    </div>
  );
}

export function Progress({ value, max, label, tone }: { value: number; max: number; label?: string; tone?: "lime" }) {
  const pct = Math.max(0, Math.min(100, max ? (100 * value) / max : 0));
  return (
    <div className={`progress ${tone ?? ""}`} role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.min(value, max)} aria-label={label}>
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children?: any }) {
  const ref = useRef<HTMLDivElement>(undefined as unknown as HTMLDivElement);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && ref.current) {
        const f = Array.from(ref.current.querySelectorAll<HTMLElement>("button, a[href], input, select, textarea, [tabindex]:not([tabindex='-1'])")).filter((x) => !x.hasAttribute("disabled"));
        if (!f.length) return;
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="modal-back" onClick={(e: any) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <div className="row between" style={{ marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <button className="btn ghost small" onClick={onClose} aria-label="Luk">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export const subjectInfo = (id: string) => SUBJECTS.find((s) => s.id === id);
export function SubjectDot({ id }: { id: string }) {
  const s = subjectInfo(id);
  return (
    <span className="pill" style={{ borderColor: s?.color, color: "#fff" }}>
      <span aria-hidden="true" style={{ display: "inline-block", width: 8, height: 8, borderRadius: 99, background: s?.color, marginRight: 6 }} />
      {s?.name ?? id}
    </span>
  );
}

export function Coins({ n }: { n: number }) {
  /* the number counts up/down instead of jumping, and the little chip gives a happy bump when you earn something */
  const shown = useTween(n, 800);
  const gained = useRecent(n, 1000);
  return (
    <span className={`chip coin ${gained ? "gain" : ""}`} title="Mønter">
      <LuCoins aria-hidden="true" size={16} />
      <span>{shown.toLocaleString("da-DK")}</span>
      <span className="sr-only"> mønter</span>
    </span>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children?: any }) {
  return (
    <label className="field">
      {label}
      {hint && <span className="hint">{hint}</span>}
      {children}
    </label>
  );
}

/** Confetti-free "victory" burst that respects reduced motion (pure CSS animation, disabled by the global rule). */
export function Count({ to, ms = 700 }: { to: number; ms?: number }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return setV(to);
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const p = Math.min(1, (t - t0) / ms);
      setV(Math.round(to * p));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, ms]);
  return <>{v.toLocaleString("da-DK")}</>;
}

export const GAME_META: Record<string, { name: string; icon: string; c1: string; c2: string; tagline: string; how: string }> = {
  monopoly: { name: "EduPoly", icon: "🏠", c1: "#7ef0a4", c2: "#15803d", tagline: "Køb gader og byg huse.", how: "Slå med terningerne og ryk rundt. Svar rigtigt for at købe en gade, betale halv leje eller bygge huse. Den rigeste spiller vinder." },
  kortkamp: { name: "Kortkamp", icon: "🃏", c1: "#ff8a6b", c2: "#ffbf47", tagline: "Farve på farve, tal på tal.", how: "Læg et kort med samme farve eller tal. Rammes du af +2 eller +4, får du et spørgsmål: Svar rigtigt og slip. Sig EDU! ved dit næstsidste kort. Først tom for kort vinder." },
  slagskibe: { name: "Sænke slagskibe", icon: "🚢", c1: "#52d6ff", c2: "#1546c4", tagline: "Find og sænk flåden.", how: "Placér din flåde på 10×10 felter. Svar rigtigt for at skyde ét skud pr. tur. Den første, der sænker alle skibe, vinder." },
  speedrun: { name: "Speedrun", icon: "⚡", c1: "#ffe866", c2: "#ff8a1f", tagline: "Svar før tiden løber ud.", how: "Rigtige svar giver point og stribebonus. Forkerte svar koster point. Hurtige svar giver ekstra." },
  fire: { name: "Fire på stribe", icon: "🔴", c1: "#9b7bff", c2: "#3fc8ee", tagline: "Fire på række vinder.", how: "Svar rigtigt for at droppe en brik. Fire på række – vandret, lodret eller på skrå – vinder." },
  skak: { name: "Skak", icon: "♟️", c1: "#7a5cff", c2: "#1c2f9a", tagline: "Skak med alle regler.", how: "Svar rigtigt for at flytte. Forkert svar betyder, at du mister dit træk. Skakmat vinder." },
  kryds: { name: "Kryds og bolle", icon: "⭕", c1: "#43e0c0", c2: "#1b6fd0", tagline: "Tre på række vinder.", how: "Svar rigtigt for at sætte dit mærke. Tre på række vinder." },
  quizduel: { name: "Quizduel", icon: "🥊", c1: "#4f8bff", c2: "#ff5c9a", tagline: "Hurtigste svar vinder.", how: "I får det samme spørgsmål samtidig. Rigtige svar giver point, og hurtige svar giver ekstra." },
  taarn: { name: "Tårnforsvar", icon: "🏰", c1: "#8bea7a", c2: "#2fa850", tagline: "Stop ballonerne med tårne.", how: "Svar rigtigt for at få guld. Køb forsvarere (5 fra start – 20 flere åbnes, når du stiger i niveau ved at spille de andre spil), opgradér dem, vælg hvem de sigter på, og start runden. Hold basen i 15 runder – og spil videre, så længe dit forsvar holder!" },
  videnraes: { name: "Vidensræs", icon: "🏁", c1: "#4cc9ff", c2: "#2faa45", tagline: "Svar dig til målstregen.", how: "Hvert rigtigt svar rykker dig frem. Forkert svar låser dig et øjeblik. Først i mål vinder." },
  skattejagt: { name: "Skattejagt", icon: "🗝️", c1: "#ff6f8a", c2: "#ffb347", tagline: "Find nøglen og skatten.", how: "Låste porte åbner kun, hvis du svarer rigtigt. Find to nøgler, åbn den store port og find skatten." },
  ludo: { name: "Ludo", icon: "🎲", c1: "#fb7185", c2: "#be123c", tagline: "Ræs rundt og kom først hjem.", how: "Svar rigtigt, så slår brættet terningen for dig. En sekser henter en brik ud og giver et ekstra slag. Slå modstanderens brikker hjem, og kom først i mål med dem begge." },
  yatzy: { name: "Yatzy", icon: "🎯", c1: "#fbbf24", c2: "#b45309", tagline: "Fem terninger, femten felter.", how: "Svar rigtigt for at få alle tre slag (forkert = kun ét). Behold de terninger, du vil, og udfyld et felt på pointarket i hver runde. Flest point efter 15 runder vinder." },
  sudoku: { name: "Sudoku", icon: "🔢", c1: "#38bdf8", c2: "#1d4ed8", tagline: "Tal, logik og tågede bokse.", how: "Fyld brættet, så hvert tal står én gang i hver række, kolonne og boks. Boksene er låst i tåge, til du svarer rigtigt på et spørgsmål. Brug et hint, hvis du sidder fast." },
  videnspar: { name: "Videnspar", icon: "🃏", c1: "#34d399", c2: "#047857", tagline: "Husk kortene, behold parrene.", how: "Vend to kort ad gangen. Er de ens, får du et spørgsmål – svar rigtigt for at beholde parret. Forkert svar vender kortene om igen. Find alle par for at vinde." },
  verden: { name: "Fri Verden", icon: "🌍", c1: "#22d3ee", c2: "#0e7490", tagline: "Kør rundt på en 3D-ø med din egen figur.", how: "Gå, løb eller kør bil rundt på øen. Tag missioner og kør ræs – ved hvert stop og efter hver omgang svarer du på spørgsmål for at komme videre. Besøg dine venner i deres verden." },
};

export const GAME_ICONS: Record<string, (p: { size?: number; "aria-hidden"?: any }) => any> = {
  monopoly: LuLandmark, kortkamp: LuLayers, slagskibe: LuShip, speedrun: LuZap, fire: LuCircleDot, skak: LuCrown,
  kryds: LuGrid3X3, quizduel: LuSwords, taarn: LuCastle, videnraes: LuFlag, skattejagt: LuGem,
  ludo: LuDices, yatzy: LuDice5, sudoku: LuLayoutGrid, videnspar: LuLayers, verden: LuGlobe,
};
/** Drawn game icon in the game's colour (one icon family, consistent stroke). */
export function GameIcon({ game, size = 54 }: { game: string; size?: number }) {
  const m = GAME_META[game];
  const I = GAME_ICONS[game] ?? LuLayers;
  return (
    <div className="gameicon" style={{ ["--c1" as any]: m?.c1, width: size, height: size }} aria-hidden="true">
      <I size={Math.round(size * 0.52)} aria-hidden="true" />
    </div>
  );
}

/** Inline coin icon for running text (replaces the coin emoji). */
export function Coin() {
  return <LuCoins aria-label="mønter" role="img" size={"1em"} className="coin-ico" />;
}
/** Logo mark. */
export function LogoMark({ size = 22 }: { size?: number }) {
  return <LuBrain aria-hidden="true" size={size} />;
}
